import { toQueryString } from '@utils/requests';

import { request } from 'api/request';
import { ACTION_TYPE } from 'api/types';

export type AIBuilderMode = 'agent' | 'plan';
export type MessageType = 'thinking' | 'compaction';

type AIBuilderProps = {
  input: string;
  journeyShortId: string;
  shortId: string;
  files?: File[];
  selectedAction?: {
    id: string;
    type: ACTION_TYPE;
  };
  mode: AIBuilderMode;
};

type AIBuilderInterruptProps = {
  journeyShortId: string;
  shortId: string;
};

type QuestionOption = {
  label: string;
  description: string;
};

export type Question = {
  question: string;
  options: QuestionOption[];
  isMultiSelect: boolean;
};

export type ResourcePickerItem = {
  id: string;
  name: string;
  subtitle?: string;
  description?: string;
  iconUrl?: string | null;
  previewUrl?: string;
};

export type ResourcePickerData = {
  resource: 'portal' | 'company_list' | 'ai_agent' | 'document_template' | 'integration_endpoint';
  title: string;
  description?: string;
  items: ResourcePickerItem[];
  emptyMessage?: string;
  selected?: ResourcePickerItem;
};

export type PortalLinkData = {
  name: string;
  description: string | null;
  url: string;
  imageUrl: string;
};

export type ToolData =
  | { type: 'questions'; questions: Question[] }
  | { type: 'plan'; plan: PlanData }
  | { type: 'resource_picker'; resourcePicker: ResourcePickerData }
  | { type: 'portal_link'; portalLink: PortalLinkData };

export type ChatMessage = {
  id: string;
  role: 'user' | 'assistant' | 'tool';
  text?: string;
  messageType?: MessageType;
  thinkingGroupId?: string;
  timeTakenSeconds?: number;
  toolName?: string;
  toolCallId?: string;
  toolLabel?: string;
  toolDescription?: string;
  status?: string;
  errorType?: string;
  errorMessage?: string;
  data?: ToolData;
};

export type ChatSessionResponse = {
  shortId: string;
  mode: AIBuilderMode;
  isGenerating: boolean;
  hasMessages?: boolean;
  hasAdminTools?: boolean;
};

export type AIBuilderExamplePrompt = {
  shortId: string;
  label: string;
  description: string;
  prompt: string;
};

export type MessagesResponse = ChatSessionResponse & {
  messages: ChatMessage[];
};

export type PaginatedMessagesResponse = MessagesResponse & {
  hasMore: boolean;
  oldestMessageId: string | null;
};

export type PaginationParams = {
  limit?: number;
  beforeId?: string;
};

type NewMessagesParams = {
  afterId: string;
  limit?: number;
};

type LoadAIBuilderNewMessagesParams = {
  journeyShortId: string;
} & NewMessagesParams;

type UpdateSessionModeProps = {
  journeyShortId: string;
  mode?: AIBuilderMode;
};

type UpdateSessionModeResponse = {
  shortId: string;
  mode: AIBuilderMode;
};

export type RunAIBuilderResponse = {
  shortId: string;
  isGenerating: boolean;
};

export const runAIBuilder = async (data: AIBuilderProps) => {
  const { input, journeyShortId, shortId, files = [], selectedAction, mode } = data;
  const formData = new FormData();

  formData.append('input', input);
  formData.append('journeyShortId', journeyShortId);
  formData.append('shortId', shortId);
  formData.append('mode', mode);

  if (selectedAction) {
    formData.append('selectedAction', JSON.stringify(selectedAction));
  }

  files.forEach((file) => {
    formData.append('files', file);
  });

  const response = await request.post<RunAIBuilderResponse>('v1/ai-builder/run/', formData);
  return response.data;
};

export const loadAIBuilderMessages = async (journeyShortId: string, params?: PaginationParams) => {
  const queryString = toQueryString({
    limit: params?.limit,
    before_id: params?.beforeId,
  });
  const response = await request.get<PaginatedMessagesResponse>(
    `v1/ai-builder/chat-messages/${journeyShortId}/${queryString}`
  );
  return response.data;
};

export const loadAIBuilderChatSession = async (journeyShortId: string) => {
  const response = await request.get<ChatSessionResponse>(
    `v1/ai-builder/chat-session/${journeyShortId}/`
  );
  return response.data;
};

export const loadAIBuilderExamplePrompts = async () => {
  const response = await request.get<AIBuilderExamplePrompt[]>('v1/ai-builder/example-prompts/');
  return response.data;
};

export const loadAIBuilderNewMessages = async (params: LoadAIBuilderNewMessagesParams) => {
  const { journeyShortId, afterId, limit } = params;
  const queryString = toQueryString({
    after_id: afterId,
    limit,
  });
  const response = await request.get<MessagesResponse>(
    `v1/ai-builder/chat-messages/${journeyShortId}/newer/${queryString}`
  );
  return response.data;
};

export const updateSessionMode = async (data: UpdateSessionModeProps) => {
  const { journeyShortId, mode } = data;
  const response = await request.patch<UpdateSessionModeResponse>(
    `v1/ai-builder/chat-messages/${journeyShortId}/`,
    { mode }
  );
  return response.data;
};

export const deleteAIBuilderHistory = async (journeyShortId: string) => {
  const response = await request.delete(`v1/ai-builder/chat-messages/${journeyShortId}/`);
  return response.data;
};

export const interruptAIBuilder = async (data: AIBuilderInterruptProps) => {
  const response = await request.post('v1/ai-builder/interrupt/', data);
  return response.data;
};

type TranscribeAudioResponse = {
  text: string;
};

export const transcribeAudio = async (audioFile: File) => {
  const formData = new FormData();
  formData.append('audioFile', audioFile);

  const response = await request.post<TranscribeAudioResponse>(
    'v1/ai-builder/transcribe/',
    formData
  );
  return response.data;
};

export type QuestionAnswer = {
  selectedOptions: string[];
  customText: string;
};

type AnswerQuestionsProps = {
  journeyShortId: string;
  shortId: string;
  answers: QuestionAnswer[];
};

export type ResumeAIBuilderResponse = {
  status: 'success' | 'accepted';
  mode?: AIBuilderMode;
  isGenerating: boolean;
};

export const answerQuestions = async (data: AnswerQuestionsProps) => {
  const { journeyShortId, shortId, answers } = data;
  const response = await request.post<ResumeAIBuilderResponse>(
    `v1/ai-builder/answer-questions/${journeyShortId}/${shortId}/`,
    { answers }
  );
  return response.data;
};

type PickResourceProps = {
  journeyShortId: string;
  shortId: string;
  resourceId: string;
};

export const pickResource = async (data: PickResourceProps) => {
  const { journeyShortId, shortId, resourceId } = data;
  const response = await request.post<ResumeAIBuilderResponse>(
    `v1/ai-builder/pick-resource/${journeyShortId}/${shortId}/`,
    {
      resourceId,
    }
  );
  return response.data;
};

// Plan types
type PlanTaskStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled';

export type TodoItem = {
  id: string;
  content: string;
  status: PlanTaskStatus;
};

export type PlanTask = {
  id: string;
  name: string;
  description: string;
  status: PlanTaskStatus;
};

export type PlanData = {
  header: string;
  shortDescription: string;
  tasks: PlanTask[];
  planDetails: string;
};

type AcceptPlanProps = {
  journeyShortId: string;
  shortId: string;
};

type AcceptPlanResponse = {
  status: 'accepted';
  mode: 'agent';
  isGenerating: boolean;
};

export const acceptPlan = async (data: AcceptPlanProps): Promise<AcceptPlanResponse> => {
  const { journeyShortId, shortId } = data;
  const response = await request.post<AcceptPlanResponse>(
    `v1/ai-builder/accept-plan/${journeyShortId}/${shortId}/`
  );
  return response.data;
};

type EnterPlanModeDecision = 'approved' | 'rejected';

type EnterPlanModeDecisionProps = {
  journeyShortId: string;
  shortId: string;
  decision: EnterPlanModeDecision;
};

type EnterPlanModeDecisionResponse = {
  status: 'accepted' | 'rejected';
  mode: AIBuilderMode;
  isGenerating: boolean;
};

export type ProviderStatusResponse = {
  status: 'ok' | 'degraded';
};

export const getProviderStatus = async () => {
  const response = await request.get<ProviderStatusResponse>('v1/ai-builder/provider-status/');
  return response.data;
};

export const decideEnterPlanMode = async (
  data: EnterPlanModeDecisionProps
): Promise<EnterPlanModeDecisionResponse> => {
  const { journeyShortId, shortId, decision } = data;
  const response = await request.post<EnterPlanModeDecisionResponse>(
    `v1/ai-builder/enter-plan-mode/${journeyShortId}/${shortId}/`,
    { decision }
  );
  return response.data;
};
